import globals from "../Globals";
import EventBus from "../EventBus";

// The site's URLs, in one place. A title's pages: /<gameId>/<page>/<sub>/<deep…>; the global ones (titles, admins,
// settings, health): /<page>/<deep…>. Pages and sub-pages are named by their string id — the menu order changes,
// the ids do not — and a page without sub-pages omits that segment. The "deep link" is whatever the page itself
// understands (a player, a version, an asset): the page reads it from globals.route.deep on mount and keeps it
// current with replaceDeep. Navigation is pushState (no reload) and PageLayout applies the location; the legacy
// hashes (#<n>-<m>-…, #player/…, #view/…) are translated once, on load, so old links keep working.

export const ROUTE_EVENT = "route";

const enc = (s) => encodeURIComponent(String(s));
const dec = (s) => { try { return decodeURIComponent(s); } catch { return s; } };

// { title, page, sub, deep } -> "/EvT/home/players/6aa0…"
export function buildPath(route) {
    const parts = [];
    if (route.title) parts.push(route.title);
    parts.push(route.page);
    if (route.sub) parts.push(route.sub);
    for (const d of route.deep ?? []) if (d != null && d !== "") parts.push(enc(d));
    return "/" + parts.join("/");
}

// "/EvT/home/players/6aa0…" -> { title, page, sub, deep } — structural only; PageLayout validates the title, the
// page and the permissions. `pages` is the registry (PageLayout.Page): global pages come first in the URL.
export function parsePath(pathname, pages) {
    const segments = pathname.split("/").filter(s => s !== "").map(dec);
    if (segments.length === 0) return null;
    const byId = (id) => Object.values(pages).find(p => p.id === id);
    let title = null, page = byId(segments[0]), rest;
    if (page && page.global) {
        rest = segments.slice(1);
    } else {
        title = segments[0];
        page = segments.length > 1 ? byId(segments[1]) : null;
        rest = segments.slice(2);
        if (!page && segments.length > 1) {
            // a page that moved into a group (sub.movedFrom = its old top-level id): the old link keeps working
            for (const group of Object.values(pages)) {
                const moved = Object.values(group.subs ?? {}).find(sub => sub.movedFrom === segments[1]);
                if (moved) return { title, page: group.id, sub: moved.id, deep: rest };
            }
        }
        if (!page) return { title, page: null, sub: null, deep: [] };
    }
    let sub = null;
    const subs = Object.values(page.subs ?? {});
    if (subs.length > 0) {
        // a renamed sub-page (sub.movedFrom = its old id inside the same page) keeps its old links too
        sub = (subs.find(s => s.id === rest[0]) ?? subs.find(s => s.movedFrom === rest[0]))?.id ?? null;
        if (sub) rest = rest.slice(1);
    }
    return { title, page: page.id, sub, deep: rest };
}

export function linkTo(route) {
    return `${window.location.origin}${buildPath(route)}`;
}

// Go somewhere: a new history entry, then PageLayout applies it (same handler as the browser's back/forward).
export function navigate(route) {
    const path = buildPath(route);
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    EventBus.emit(ROUTE_EVENT);
}

// The current location, rewritten in place (no history entry, no re-render): PageLayout's fallbacks.
export function replace(route) {
    const path = buildPath(route);
    if (window.location.pathname !== path) window.history.replaceState(null, "", path);
}

// A page's own parameters changed (a version picked, a player loaded): keep the URL shareable without touching
// the history or re-rendering anything.
export function replaceDeep(deep) {
    const route = globals.route;
    if (!route) return;
    route.deep = (deep ?? []).map(d => d == null ? "" : String(d));
    while (route.deep.length > 0 && route.deep[route.deep.length - 1] === "") route.deep.pop();
    replace(route);
}

// Legacy hashes -> routes. #<page>-<sub>[-<deep…>] used the numeric ids of the old registry; #player/<title>/<id>
// and #view/<title>/<kind>/<branch>/<M.m>/<patch>[/<diff>] were the deep links mailed and copied around.
const LEGACY = {
    0: { page: "admins", subs: { 0: { page: "admins" }, 2: { page: "settings" } } },
    1: { page: "titles" },
    9: { page: "health" },
    2: { page: "home", sub: "overview" },
    3: { page: "home", sub: "players" },
    8: { page: "home", sub: "purchases" },
    4: { page: "servers", subs: { 0: "overview", 1: "specs", 2: "fleets", 3: "machines", 4: "logs" } },
    5: { page: "database", subs: { 0: "versioning", 1: "assets", 2: "inspector" } },
    6: { page: "test" },
    7: { page: "development", subs: { 6: "overview", 0: "vision", 10: "memories", 1: "agents", 2: "messages", 3: "decisions",
                                       4: "backlog", 5: "hitl", 7: "cost", 8: "simulations", 9: "settings" } },
};
const GLOBAL_LEGACY = new Set(["admins", "settings", "titles", "health"]);

export function translateLegacyHash(hash, selectedTitle) {
    const body = (hash ?? "").replace(/^#/, "");
    if (!body) return null;
    if (body.startsWith("player/")) {
        const [, title, player] = body.split("/");
        return title && player ? { title, page: "home", sub: "players", deep: [player] } : null;
    }
    if (body.startsWith("view/")) {
        const [, title, kind, branch, version, patch, diff] = body.split("/");
        if (!title || !kind || !branch || !version || !patch) return null;
        return { title, page: "database", sub: "versioning", deep: [branch, version, "view", kind, patch, ...(diff ? [diff] : [])] };
    }
    const parts = body.split("-");
    const entry = LEGACY[parseInt(parts[0], 10)];
    if (!entry) return null;
    let page = entry.page, sub = entry.sub ?? null;
    if (entry.subs) {
        const s = entry.subs[parseInt(parts[1], 10)] ?? Object.values(entry.subs)[0];
        if (typeof s === "object") page = s.page; else sub = s;
    }
    const title = GLOBAL_LEGACY.has(page) ? null : selectedTitle;
    let deep = parts.slice(2).map(dec);
    // The old versioning deep link carried the compact version number; the new one says M.m.
    if (page === "database" && sub === "versioning" && deep.length >= 2 && /^\d+$/.test(deep[1])) {
        const { major, minor } = globals.decompactVersion(Number(deep[1]));
        deep[1] = `${major}.${minor}`;
    }
    return { title, page, sub, deep };
}
